// src/components/PurchaseFlow.js
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, collection, addDoc, onSnapshot, getDoc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { toast } from 'react-toastify';
import CONFIG from '../config';

// Set a timeout for when we should show the long wait message
const LONG_WAIT_THRESHOLD = 3000; // 3 seconds

function PurchaseFlow() {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan'); // "sixMonth" or "lifetime"
  const navigate = useNavigate();
  const [loadingTime, setLoadingTime] = useState(0);
  const [showLongWaitMessage, setShowLongWaitMessage] = useState(false);

  useEffect(() => {
    // Force a full page refresh if needed
    const forceRefresh = searchParams.get('refresh');
    if (forceRefresh === 'true') {
      window.location.href = `/purchase?plan=${plan}`;
      return;
    }

    // Check if we're on a direct purchase from a new signup
    const isNewSignup = searchParams.get('newSignup') === 'true';
    const isDirectPurchase = window.location.pathname.includes('/purchase-direct');

    // Get the current path to determine if this is the direct purchase route
    if (!auth.currentUser) {
      // Only redirect if not a direct purchase from new signup
      if (!isDirectPurchase) {
        toast.error('You must be signed in to purchase.');
        navigate('/login');
        return;
      }
      
      // For direct purchase, give auth time to initialize
      console.log("New signup detected, waiting for auth to initialize...");
      const authCheckInterval = setInterval(() => {
        if (auth.currentUser) {
          clearInterval(authCheckInterval);
          // Continue with purchase flow once auth is initialized
          if (plan && CONFIG.tiers[plan]) {
            checkBetaAccess(plan);
          }
        }
      }, 500);
      
      // Clear interval after 10 seconds to prevent memory leaks
      setTimeout(() => clearInterval(authCheckInterval), 10000);
      return;
    }
    
    // Check if plan is valid
    if (!plan || !CONFIG.tiers[plan]) {
      toast.error('Invalid or missing plan.');
      navigate('/');
      return;
    }
    
    // Start timer to track loading time
    const startTime = Date.now();
    setLoadingTime(0);
    
    // Set an interval to update the loading time
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setLoadingTime(elapsed);
      
      // If it's taking too long, show the long wait message
      if (elapsed > LONG_WAIT_THRESHOLD && !showLongWaitMessage) {
        setShowLongWaitMessage(true);
      }
    }, 500);
    
    // Add a console log to verify this version is running
    console.log('PurchaseFlow component initialized with plan:', plan, '- Version: ' + new Date().toISOString());
    
    // Start by checking if the user has beta access
    checkBetaAccess(plan);
    
    // Clean up the timer
    return () => clearInterval(timer);
  }, [plan, navigate, searchParams]);
  
  // Check if the current user has beta access
  const checkBetaAccess = async (planKey) => {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        createCheckoutSession(planKey);
        return;
      }
      
      const userEmail = user.email.toLowerCase();
      
      // Check if the user's email is in the beta_users collection
      const betaUserRef = doc(db, 'beta_users', userEmail);
      const betaUserDoc = await getDoc(betaUserRef);
      
      if (betaUserDoc.exists() && betaUserDoc.data().active === true) {
        console.log(`User has beta access with tier: ${betaUserDoc.data().tier}`);
        // Grant beta access without using Cloud Functions
        await grantBetaAccessManually(betaUserDoc.data().tier);
      } else {
        // Not a beta user, proceed with normal checkout
        console.log('User is not a beta user, proceeding with normal checkout');
        createCheckoutSession(planKey);
      }
    } catch (error) {
      console.error('Error checking beta access:', error);
      // Fall back to normal checkout
      createCheckoutSession(planKey);
    }
  };
  
  // Grant beta access manually
  const grantBetaAccessManually = async (tier) => {
    try {
      const user = auth.currentUser;
      const userRef = doc(db, 'users', user.uid);
      
      if (tier === 'lifetime') {
        await updateDoc(userRef, {
          subscriptionTier: 'lifetime',
          purchaseDate: serverTimestamp(),
          isBetaUser: true,
          expirationDate: deleteField()
        });
      } else if (tier === 'sixMonth') {
        const sixMonthsFromNow = new Date();
        sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
        
        await updateDoc(userRef, {
          subscriptionTier: 'sixMonth',
          purchaseDate: serverTimestamp(),
          isBetaUser: true,
          expirationDate: sixMonthsFromNow
        });
      }
      
      toast.success(`You've been granted ${tier} access as a beta user!`);
      navigate('/profile?from=beta');
    } catch (error) {
      console.error('Error granting beta access:', error);
      toast.error('Error activating beta access');
      // Fall back to normal checkout
      createCheckoutSession(plan);
    }
  };

  const createCheckoutSession = async (planKey) => {
    const user = auth.currentUser;
    
    // Special handling for free tier
    if (planKey === 'free') {
      try {
        const userRef = doc(db, 'users', user.uid);
        
        await updateDoc(userRef, {
          subscriptionTier: 'free',
          purchaseDate: serverTimestamp(),
          videoCount: 0
        });
        
        // Track free plan conversion with Google Analytics
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'G-XMGH0SPFBV',
            'event_category': 'subscription',
            'event_label': 'free_plan_activated',
            'value': 0
          });
        }
        
        toast.success('You\'ve been enrolled in the free plan!');
        navigate('/dashboard');
        return;
      } catch (error) {
        console.error('Error activating free tier:', error);
        toast.error('Error activating free plan');
        return;
      }
    }
    
    // For paid plans, grab the real Stripe price ID from config
    const priceId = CONFIG.tiers[planKey].stripePriceId; 
    if (!priceId) {
      toast.error('No Stripe price configured for this plan.');
      return;
    }

    // Pre-fetch the Stripe.js script to speed up redirect
    const stripeScript = document.createElement('script');
    stripeScript.src = 'https://js.stripe.com/v3/';
    document.body.appendChild(stripeScript);

    try {
      // Start a timestamp to measure performance
      const startTime = performance.now();
      
      // Use the Invertase extension
      const checkoutRef = collection(doc(db, 'customers', user.uid), 'checkout_sessions');
      const docRef = await addDoc(checkoutRef, {
        price: priceId,
        allow_promotion_codes: true,
        success_url: window.location.origin + '/profile?from=purchase',
        cancel_url: window.location.origin,
        mode: 'payment',
      });

      // Log how long it took to create the checkout session
      console.log(`Checkout session creation took ${performance.now() - startTime}ms`);

      // Add a timeout to navigate away if taking too long
      const maxWaitTime = setTimeout(() => {
        toast.error('Checkout is taking longer than expected. Please try again.');
        navigate('/');
      }, 15000); // 15 seconds max wait time

      onSnapshot(docRef, (snap) => {
        // Clear the timeout since we got a response
        clearTimeout(maxWaitTime);
        
        const { error, url } = snap.data() || {};
        if (error) {
          toast.error(`Checkout error: ${error.message}`);
          console.error(error);
        }
        if (url) {
          // Track Stripe checkout redirect with Google Analytics
          if (window.gtag) {
            window.gtag('event', 'begin_checkout', {
              'send_to': 'G-XMGH0SPFBV',
              'event_category': 'ecommerce',
              'event_label': planKey,
              'value': CONFIG.tiers[planKey]?.price || 0
            });
          }
          
          // Log total time before redirect
          console.log(`Total time before redirect: ${performance.now() - startTime}ms`);
          window.location.assign(url);
        }
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Could not start checkout session.');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin mb-4"></div>
      
      <p className="text-xl font-semibold mb-2">Preparing your purchase...</p>
      <p className="text-sm text-green-600 mb-4">Checkout v{new Date().toISOString().split('T')[0]}</p>
      
      {showLongWaitMessage && (
        <div className="max-w-md text-center mt-4">
          <p className="text-gray-600 mb-2">
            This is taking a bit longer than expected. We're setting up your checkout session with Stripe.
          </p>
          <p className="text-gray-600">
            You'll be redirected automatically when it's ready (wait time: {Math.floor(loadingTime/1000)}s)
          </p>
          
          <div className="mt-6">
            <button 
              onClick={() => window.location.reload(true)}
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition"
            >
              Force Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PurchaseFlow;